/* @HEADER
 * @version 1.1.1442 | 2026-11-02
 * @purpose Owns the active-project switch lifecycle (ZVX-DEV-2745): ProjectSwitcher, nested deep inside the authenticated app, asks for an in-place switch; the host rebinds the ambient database, seeds it, and remounts the app under a new key while an overlay covers the gap until the new project's first data load. Out of App.tsx with its own proof (ZVX-DEV-6146, App.tsx-by-meaning S6); the app to host is a prop, so this file imports nothing from App.tsx.
 * @sidecar AuthenticatedAppHost.tsx.header.md
 * @layer app | @hex adapter | @ctx projects
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8851, ZVX-DEV-8169, ZVX-DEV-8168, ZVX-DEV-8073, ZVX-DEV-6146, ZVX-DEV-2745, ZVX-DEV-5814, ZVX-DEV-7143, ZVX-DEV-7440

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { flushSync } from 'react-dom'
import { ProjectSwitchProvider } from './ProjectSwitchContext'
import { ProjectDeletedBanner, createProjectDeletedNotice } from './ProjectDeletedBanner'
import { createCardRestoreQuestions } from '../features/content-p2p/cardRestoreQuestions.mjs'
import { createProjectRemovalLedger } from '../lib/projectRemovalLedger.ts'
import { ProjectSwitchOverlay } from './ProjectSwitchOverlay'
import { getActiveProject, getActiveProjectId, setActiveProjectId, resolveProjectPrefix, type ProjectRecord } from '../lib/projectStore'
import { getDbName, setDbName, setProjectKey, resolveDbName, withDbLock } from '../lib/db'
import { ensureDefaultBoardGroup, ensureDefaultBoard, ensureAllTasksSpace, ensureAllTasksDefaultBoard } from '../lib/db'
import { switchAmbientProject } from '../lib/db/switchAmbientProject'
import { AMBIENT_POINTER_REBOUND_EVENT, readAmbientPointerReboundDetail } from '../lib/db/ambientPointerRebind'
// @ts-expect-error -- plain .mjs helper with no local .d.ts; its JSDoc gives the shape.
import { emitCatalogReset } from '../features/content-p2p/catalogResetSignal.mjs'

/** The overlay never outlives this, even when the new project's first load never reports ready. */
export const SWITCH_OVERLAY_CEILING_MS = 4_000

export interface AuthenticatedAppHostProps {
  /** The authenticated app to mount under the active project's key; it reports its first data load through `onReady`. */
  app: ComponentType<{ onReady?: () => void }>
}

/**
 * ZVX-DEV-2745: `getActiveData()` always reads IndexedDB fresh (no in-memory
 * cache), so a keyed remount of the app against the newly-active dbName is
 * sufficient to pick up the new project's data — the old subtree unmounts the
 * instant the key changes, so old boards can never leak onto the new
 * project's screen. A full-screen overlay covers the gap between «switch
 * requested» and «new project's first data load resolved» (`onReady`).
 */
export function AuthenticatedAppHost({ app: App }: AuthenticatedAppHostProps) {
  const [activeProjectId, setActiveKey] = useState(() => getActiveProjectId())
  // ZVX-DEV-7440 -- the database the app was mounted against, the second half of
  // its key. It starts as whatever the pointer holds now and moves only with a
  // switch (below) or with the registry's first cycle re-binding the pointer.
  const [dbKey, setDbKey] = useState(() => getDbName())
  const [switchingName, setSwitchingName] = useState<string | null>(null)

  // ZVX-DEV-8168 (ZVX-4464 item 7) -- while a switch runs NO app tree is mounted. The pointer and the database name move inside it,
  // and a tree mounted beside a pointer that names another project addresses the wrong project with everything it does. A keyed
  // remount cannot close that window by itself: the new tree would mount on the OLD pointer. So the old tree is taken away first
  // (the pointer still names its own project while its cleanups run), the pointer and the name move with nothing mounted, and the
  // new tree is mounted last, on both. The overlay already covers the screen; it is up for the whole pause.
  const [paused, setPaused] = useState(false)
  // Two switches asked in a row run one after the other, never interleaved.
  const switchChain = useRef<Promise<unknown>>(Promise.resolve())

  const runSwitch = useCallback(async (project: ProjectRecord) => {
    setSwitchingName(project.name) // overlay ON immediately — no blank/old-screen gap.
    // The unmount has happened when flushSync returns, cleanups included: only then does anything move.
    flushSync(() => setPaused(true))
    try {
      setActiveProjectId(project.id)
      setProjectKey(resolveProjectPrefix(project)) // ZVX-DEV-3221: Latin initials/prefix fallback, not the literal 'ZVX'
      // ZVX-DEV-5814 -- the name change and the seeding that depends on it happen
      // INSIDE `withDbLock`, the FIFO queue every other ambient mutator takes, so
      // a section already holding the lock never runs against a name that moved.
      // Best-effort seeding: the app's own seed effect re-runs the same ensure*
      // calls after remount and clears the overlay regardless.
      await switchAmbientProject({
        dbName: resolveDbName(project),
        setDbName,
        withDbLock,
        seed: () =>
          Promise.all([ensureDefaultBoardGroup(), ensureDefaultBoard(), ensureAllTasksSpace(), ensureAllTasksDefaultBoard()]),
      })
      // ZVX-DEV-7143 -- a switch no longer stamps sync_id (it did since
      // ZVX-DEV-3280): see bootTaskMaintenance.ts for why and what signs instead.
      // The key names the project and database the pointer now holds. Both halves
      // and the end of the pause in ONE batch (ZVX-DEV-7440): a switch is exactly one
      // remount, and the registry's first cycle announcing that same name afterwards
      // remakes nothing a second time.
      setActiveKey(project.id)
      setDbKey(resolveDbName(project))
      // ZVX-4386 -- what anything remembered about the catalog was for the project (and so the organisation) just left.
      emitCatalogReset('scope-change', { source: 'switch', projectId: project.id })
    } catch (error) {
      // The move failed part-way: the pointer may already name the new project. Mount a tree that matches whatever the pointer
      // and the name are NOW rather than a blank host, and tell the caller.
      setActiveKey(getActiveProjectId())
      setDbKey(getDbName())
      throw error
    } finally {
      setPaused(false)
    }
  }, [])

  const switchProject = useCallback((project: ProjectRecord) => {
    const run = switchChain.current.then(() => runSwitch(project))
    switchChain.current = run.catch(() => undefined)
    return run
  }, [runSwitch])

  // ZVX-DEV-8169 (ZVX-4464 item 23) -- the registry's first cycle moves the ambient pointer under a mounted app. It used to bind the
  // name itself and announce it, and the host remade the app on the announcement: between the two the tree stood beside a pointer
  // that already named another database. The host is the only mover now: the registry asks, and the host makes the pause a switch
  // makes -- the tree is taken away, the name is bound inside the ambient lock with nothing mounted, and the tree is mounted again
  // against it. Chained with switches so the two never interleave; a switch ahead of this request may already have bound the name,
  // which is the re-check below (and the reason nothing is announced twice).
  const runRebind = useCallback(async (dbName: string) => {
    if (getDbName() === dbName) return
    setSwitchingName(getActiveProject().name) // overlay ON, as a switch does
    flushSync(() => setPaused(true))
    try {
      // The one mover of the ambient name inside the lock (ZVX-DEV-5814), with nothing to seed: the new tree repeats its own.
      await switchAmbientProject({ dbName, setDbName, withDbLock })
    } finally {
      // The key names the database the pointer holds NOW, whether or not the bind succeeded.
      setDbKey(getDbName())
      setPaused(false)
    }
    // ZVX-4386 -- the same for a re-bind: the app is remade on another database.
    emitCatalogReset('scope-change', { source: 'registry-rebind', dbName })
  }, [])

  const rebindAmbientPointer = useCallback((dbName: string) => {
    const run = switchChain.current.then(() => runRebind(dbName))
    switchChain.current = run.catch(() => undefined)
    return run
  }, [runRebind])

  // ZVX-DEV-8226 -- what is kept about a project deleted on another device lives as long as this host: made here, once, and
  // handed to the registry cycle and to the move through the switch API. Nothing of it is held by a module.
  const [removalLedger] = useState(createProjectRemovalLedger)
  const [deletedNotice] = useState(createProjectDeletedNotice)
  // ZVX-DEV-8851 -- the questions about cards deleted after their person edited them; like the notice above, held by this host and by nothing else.
  const [cardRestoreQuestions] = useState(createCardRestoreQuestions)

  const switchApi = useMemo(
    () => ({ switchProject, rebindAmbientPointer, removalLedger, deletedNotice, cardRestoreQuestions }),
    [switchProject, rebindAmbientPointer, removalLedger, deletedNotice, cardRestoreQuestions],
  )

  // ZVX-DEV-7440 -- the pointer can be re-bound from UNDER the mounted app: the
  // registry's answer (ZVX-DEV-6737) re-resolves it after sign-in, and everything
  // the app resolved from the pointer at mount keeps the old answer for the rest
  // of its life (BoardP2pAutoSyncMount pins its database names at mount and says
  // so). The key above is what makes «one mount, one project» true, but only a
  // switch changed it -- so a re-bind nobody asked the host about left the app
  // running on the pre-answer name. The hook announces a re-bind that really
  // moved the pointer; the host remakes the app on it.
  //
  // Only the registry's FIRST cycle of the session, and only for a different
  // name. That is the cycle that can move the pointer under an app that mounted
  // before the registry answered, and at that moment nothing the person has done
  // is at stake yet. A later cycle re-binds only when the active project has
  // vanished from the registry; throwing away an open screen for that is a bigger
  // harm than the stale name, so it moves the pointer and leaves the app alone
  // (follow-up on the card).
  //
  // Not on `onReady`: that is the app's first load, and whether it comes before
  // or after the pointer moves depends on whether IndexedDB or the network
  // answers first -- ordering by latency. The announcement is an event with a
  // meaning of its own. See
  // docs/backlog/the-host-remakes-the-app-when-the-registry-moves-the-pointer.md.
  useEffect(() => {
    function onPointerRebound(event: Event) {
      const detail = readAmbientPointerReboundDetail(event)
      if (detail !== null && detail.firstCycle && detail.dbName !== dbKey) {
        setSwitchingName(getActiveProject().name) // overlay ON, as a switch does
        setDbKey(detail.dbName)
        // ZVX-4386 -- the same for a re-bind: the app is remade on another database.
        emitCatalogReset('scope-change', { source: 'registry-rebind', dbName: detail.dbName })
      }
    }
    window.addEventListener(AMBIENT_POINTER_REBOUND_EVENT, onPointerRebound)
    return () => window.removeEventListener(AMBIENT_POINTER_REBOUND_EVENT, onPointerRebound)
  }, [dbKey])

  useEffect(() => {
    // The ceiling counts from the mount of the new tree: while nothing is mounted the overlay is all the person sees.
    if (switchingName === null || paused) return
    const timer = setTimeout(() => setSwitchingName(null), SWITCH_OVERLAY_CEILING_MS)
    return () => clearTimeout(timer)
  }, [switchingName, paused])

  return (
    <ProjectSwitchProvider value={switchApi}>
      {!paused && <App key={`${activeProjectId}@${dbKey}`} onReady={() => setSwitchingName(null)} />}
      {switchingName !== null && <ProjectSwitchOverlay name={switchingName} />}
      {/* ZVX-DEV-8226 -- outside the remade App: the notice survives the remount and is visible over the empty-organisation screen. */}
      <ProjectDeletedBanner store={deletedNotice} />
    </ProjectSwitchProvider>
  )
}
